import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '../../hooks/useUserRole';
import { authService } from '../../services/authService';
import Layout from '../../components/Layout/Layout';
import Button from '../../components/Common/Button';
import {
    FaUser,
    FaEnvelope,
    FaPhone,
    FaLock,
    FaEye,
    FaEyeSlash,
    FaArrowLeft,
    FaCheck,
    FaShieldAlt,
    FaUserPlus,
    FaExclamationCircle,
    FaCheckCircle,
    FaTimesCircle
} from 'react-icons/fa';
import styles from './styles/CreateAdminPage.module.css';

export default function CreateAdminPage() {
    const navigate = useNavigate();
    const { isAdmin, loading: roleLoading } = useUserRole();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Vérifier que l'utilisateur est admin
    if (roleLoading) {
        return (
            <Layout>
                <div className={styles.container}>
                    <div className={styles.loadingState}>
                        <div className={styles.spinner}></div>
                        <p>Vérification des permissions...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!isAdmin) {
        return (
            <Layout>
                <div className={styles.container}>
                    <div className={styles.accessDenied}>
                        <div className={styles.deniedIcon}>
                            <FaTimesCircle />
                        </div>
                        <h2>Accès refusé</h2>
                        <p>Vous n'avez pas les permissions pour accéder à cette page.</p>
                        <Button onClick={() => navigate('/')} className={styles.backBtn}>
                            <FaArrowLeft /> Retour à l'accueil
                        </Button>
                    </div>
                </div>
            </Layout>
        );
    }

    // Gérer les changements du formulaire
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    // Créer un nouvel admin
    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);

        try {
            console.log('Création nouvel admin');

            // Validation
            if (!formData.name || !formData.email || !formData.password) {
                throw new Error('Tous les champs obligatoires doivent être remplis');
            }

            if (formData.password !== formData.confirmPassword) {
                throw new Error('Les mots de passe ne correspondent pas');
            }

            if (formData.password.length < 6) {
                throw new Error('Le mot de passe doit contenir au moins 6 caractères');
            }

            setLoading(true);

            // Créer l'utilisateur avec le rôle admin
            await authService.createAdmin({
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                password: formData.password,
            });

            console.log('Admin créé avec succès');
            setSuccess(true);
            setFormData({
                name: '',
                email: '',
                phone: '',
                password: '',
                confirmPassword: '',
            });

            // Rediriger après 2 secondes
            setTimeout(() => {
                navigate('/admin/reservations');
            }, 2000);
        } catch (err) {
            console.error('Erreur:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <div className={styles.createAdminContainer}>
                {/* Header */}
                <div className={styles.pageHeader}>
                    <div className={styles.headerContent}>
                        <div className={styles.headerIcon}>
                            <FaUserPlus />
                        </div>
                        <div>
                            <h1 className={styles.pageTitle}>Créer un nouvel administrateur</h1>
                            <p className={styles.pageSubtitle}>Ajouter un administrateur au système OV Hairstyle</p>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className={styles.contentWrapper}>
                    <div className={styles.formSection}>
                        <div className={styles.card}>
                            {/* Success Message */}
                            {success && (
                                <div className={styles.successMessage}>
                                    <FaCheckCircle className={styles.messageIcon} />
                                    <div>
                                        <strong>Administrateur créé avec succès!</strong>
                                        <p>Redirection en cours...</p>
                                    </div>
                                </div>
                            )}

                            {/* Error Message */}
                            {error && (
                                <div className={styles.errorMessage}>
                                    <FaTimesCircle className={styles.messageIcon} />
                                    <div>
                                        <strong>Erreur</strong>
                                        <p>{error}</p>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleCreateAdmin} className={styles.form}>
                                {/* Nom */}
                                <div className={styles.formGroup}>
                                    <label htmlFor="name">Nom complet *</label>
                                    <div className={styles.inputWrapper}>
                                        <FaUser className={styles.inputIcon} />
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="Ex: Jean Dupont"
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                {/* Email */}
                                <div className={styles.formGroup}>
                                    <label htmlFor="email">Email *</label>
                                    <div className={styles.inputWrapper}>
                                        <FaEnvelope className={styles.inputIcon} />
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="Ex: admin@example.com"
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                {/* Téléphone */}
                                <div className={styles.formGroup}>
                                    <label htmlFor="phone">Téléphone (optionnel)</label>
                                    <div className={styles.inputWrapper}>
                                        <FaPhone className={styles.inputIcon} />
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="Ex: +33612345678"
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                {/* Mot de passe */}
                                <div className={styles.formGroup}>
                                    <label htmlFor="password">Mot de passe *</label>
                                    <div className={styles.inputWrapper}>
                                        <FaLock className={styles.inputIcon} />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            id="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder="Minimum 6 caractères"
                                            required
                                            disabled={loading}
                                        />
                                        <button
                                            type="button"
                                            className={styles.togglePassword}
                                            onClick={() => setShowPassword(!showPassword)}
                                            disabled={loading}
                                        >
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                    <small className={styles.hint}>
                                        Minimum 6 caractères
                                    </small>
                                </div>

                                {/* Confirmer mot de passe */}
                                <div className={styles.formGroup}>
                                    <label htmlFor="confirmPassword">Confirmer le mot de passe *</label>
                                    <div className={styles.inputWrapper}>
                                        <FaLock className={styles.inputIcon} />
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            placeholder="Confirmer le mot de passe"
                                            required
                                            disabled={loading}
                                        />
                                        <button
                                            type="button"
                                            className={styles.togglePassword}
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            disabled={loading}
                                        >
                                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                </div>

                                {/* Boutons */}
                                <div className={styles.actions}>
                                    <Button
                                        type="submit"
                                        size="large"
                                        loading={loading}
                                        disabled={loading}
                                        className={styles.submitBtn}
                                    >
                                        <FaCheck /> Créer l'administrateur
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="large"
                                        onClick={() => navigate('/admin/reservations')}
                                        disabled={loading}
                                        className={styles.cancelBtn}
                                    >
                                        <FaArrowLeft /> Annuler
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Info Section */}
                    <div className={styles.infoSection}>
                        <div className={styles.infoCard}>
                            <div className={styles.infoHeader}>
                                <FaShieldAlt className={styles.infoIcon} />
                                <h3>Permissions administrateur</h3>
                            </div>
                            <ul className={styles.permissionsList}>
                                <li>
                                    <FaCheck className={styles.checkIcon} />
                                    <span>Accès à toutes les fonctionnalités admin</span>
                                </li>
                                <li>
                                    <FaCheck className={styles.checkIcon} />
                                    <span>Confirmation des réservations</span>
                                </li>
                                <li>
                                    <FaCheck className={styles.checkIcon} />
                                    <span>Envoi des PDF aux clients</span>
                                </li>
                                <li>
                                    <FaCheck className={styles.checkIcon} />
                                    <span>Gestion du tableau de bord</span>
                                </li>
                                <li>
                                    <FaCheck className={styles.checkIcon} />
                                    <span>Création d'autres administrateurs</span>
                                </li>
                            </ul>
                        </div>

                        <div className={styles.warningCard}>
                            <div className={styles.warningHeader}>
                                <FaExclamationCircle className={styles.warningIcon} />
                                <h3>Important</h3>
                            </div>
                            <p>
                                Les administrateurs ont un accès complet au système. Assurez-vous que vous faites confiance à cette personne avant de créer son compte.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
